class GuidedBlender extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.strings = JSON.parse(this.querySelector('[data-gb-strings]').textContent);
    this.root = this.dataset.root || '/';
    this.state = { components: [], size: '120ml', dank: 'Clean', name: '', quantity: 1, panel: 0 };
    this.flavors = [];
    this.variants = [];
    this.byHandle = new Map();
    this.filter = 'all';
    this.query = '';
    this.busy = false;
    this.documents = JSON.parse(this.el('documents').textContent);
    this.documentTab = 'guide';
    this.documentReport = null;
    this.boundReaderClose = this.onReaderClose.bind(this);
    this.boundReaderKeydown = this.onReaderKeydown.bind(this);
    this.el('reader').addEventListener('close', this.boundReaderClose);
    this.el('reader').addEventListener('keydown', this.boundReaderKeydown);
    this.boundClick = this.onClick.bind(this);
    this.boundInput = this.onInput.bind(this);
    this.addEventListener('click', this.boundClick);
    this.addEventListener('input', this.boundInput);
    this.addEventListener('change', this.boundInput);
    this.load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundClick);
    this.removeEventListener('input', this.boundInput);
    this.removeEventListener('change', this.boundInput);
    this.el('reader').close();
    this.el('reader').removeEventListener('close', this.boundReaderClose);
    this.el('reader').removeEventListener('keydown', this.boundReaderKeydown);
    this.initialized = false;
  }

  el(name) { return this.querySelector(`[data-gb-${name}]`); }
  all(name) { return [...this.querySelectorAll(`[data-gb-${name}]`)]; }
  text(key, values = {}) {
    return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), this.strings[key] || key);
  }
  escape(value) { return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }
  icon(name) { return this.el(name)?.innerHTML || ''; }
  money(cents) { return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100); }
  fmtMl(ml) { return `${Number(ml.toFixed(2))}ml`; }
  get ready() { return this.state.components.length >= 2 && this.state.components.length <= 6; }
  get variant() { return this.variants.find((v) => v.option1 === this.state.size && v.option2 === this.state.dank && v.available); }
  get ml() { return Number.parseFloat(this.state.size); }
  get dankPct() { return { Clean: 0, Dank: 3, 'Super Dank': 8 }[this.state.dank]; }

  async json(url, options) {
    let response;
    try { response = await fetch(url, options); }
    catch { throw new Error(this.text('request_error')); }
    const body = await response.json().catch(() => null);
    if (!response.ok || !body) throw new Error(body?.description || body?.message || this.text('request_error'));
    return body;
  }

  async load() {
    try {
      if (!this.dataset.productUrl) throw new Error(this.text('catalog_error'));
      const [index, product, cart] = await Promise.all([this.json(this.dataset.flavorsUrl), this.json(`${this.dataset.productUrl}.js`), this.json(`${this.root}cart.js`).catch(() => null)]);
      this.customCartQuantity = cart?.items.filter((item) => item.handle === 'custom-terpene-blend').reduce((total, item) => total + item.quantity, 0) || 0;
      this.imageBase = index.imageBase;
      this.flavors = index.flavors.filter((f) => f.available && f.img);
      const featured = ['atf-terpenes', 'mango', 'blue-dream-terpenes', 'zkittles-terpenes-1'];
      this.flavors.sort((a, b) => (featured.includes(a.handle) ? featured.indexOf(a.handle) : 99) - (featured.includes(b.handle) ? featured.indexOf(b.handle) : 99));
      this.byHandle = new Map(this.flavors.map((flavor) => [flavor.handle, flavor]));
      this.variants = product.variants.filter((v) => ['Clean', 'Dank', 'Super Dank'].includes(v.option2) && Number.parseFloat(v.option1) > 0);
      if (!this.variants.some((v) => v.available)) throw new Error(this.text('catalog_error'));
      const sizes = [...new Set(this.variants.map((v) => v.option1))].sort((a, b) => Number.parseFloat(a) - Number.parseFloat(b));
      this.el('size').innerHTML = sizes.map((size) => `<option value="${this.escape(size)}">${this.escape(size)}</option>`).join('');
      this.el('profiles').innerHTML = ['Clean', 'Dank', 'Super Dank'].map((profile, i) => `<label><input type="radio" name="gb-profile-${this.id}" data-gb-profile value="${profile}" ${i === 0 ? 'checked' : ''}><span>${this.escape(this.text(['clean', 'dank', 'super_dank'][i]))}<small>${this.escape(this.text(['all_flavor', 'light', 'strong'][i]))}</small></span></label>`).join('');
      this.restore();
      if (!sizes.includes(this.state.size)) this.state.size = sizes[0];
      this.el('size').value = this.state.size;
      this.el('name').value = this.state.name;
      this.all('profile').forEach((radio) => { radio.checked = radio.value === this.state.dank; });
      this.el('loading').hidden = true;
      this.render();
      this.showPanel(this.state.panel, false);
    } catch (error) {
      this.el('loading').hidden = true;
      this.showError(this.text('load_error'));
      console.error('[guided-blender] load failed', error.message);
    }
  }

  save() {
    try { sessionStorage.setItem('tsl-guided-draft-v1', JSON.stringify(this.state)); } catch {}
  }

  restore() {
    try {
      const draft = JSON.parse(sessionStorage.getItem('tsl-guided-draft-v1'));
      if (!draft || !Array.isArray(draft.components) || draft.components.length > 6) return;
      const components = draft.components.filter((c) => this.byHandle.has(c.handle) && Number.isInteger(c.pct) && c.pct >= 5 && c.pct <= 100);
      if (new Set(components.map((c) => c.handle)).size !== components.length) return;
      if (components.length && components.reduce((sum, c) => sum + c.pct, 0) !== 100) return;
      this.state.components = components;
      if (this.variants.some((v) => v.option1 === draft.size)) this.state.size = draft.size;
      if (['Clean', 'Dank', 'Super Dank'].includes(draft.dank)) this.state.dank = draft.dank;
      this.state.name = String(draft.name || '').slice(0, 60);
      this.state.quantity = draft.quantity === 2 ? 2 : 1;
      this.state.panel = this.ready && [0, 1, 2].includes(draft.panel) ? draft.panel : 0;
    } catch {}
  }

  onClick(event) {
    if (event.target === this.el('reader')) {
      const bounds = this.el('reader').getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.el('reader').close();
      return;
    }
    const button = event.target.closest('button');
    if (!button || button.disabled || this.busy) return;
    if (button.hasAttribute('data-gb-open-reader')) this.openReader(button.dataset.gbOpenReader, button);
    if (button.hasAttribute('data-gb-close-reader')) this.el('reader').close();
    if (button.hasAttribute('data-gb-document-tab')) this.showDocumentTab(button.dataset.gbDocumentTab);
    if (button.hasAttribute('data-gb-report')) this.showReport(button.dataset.gbReport);
    if (button.hasAttribute('data-gb-download-report')) this.downloadReport(button.dataset.gbDownloadReport, button);
    if (button.hasAttribute('data-gb-reports-back')) this.showDocumentTab('coa');
    if (button.hasAttribute('data-gb-document-zoom')) {
      const zoomed = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(zoomed));
      button.textContent = this.text(zoomed ? 'document_fit' : 'document_zoom');
      this.el('document-pages').classList.toggle('is-zoomed', zoomed);
      this.el('document-zoom-hint').hidden = !zoomed;
    }
    if (button.hasAttribute('data-gb-pick')) this.toggle(button.dataset.gbPick);
    if (button.hasAttribute('data-gb-remove')) this.toggle(button.dataset.gbRemove);
    if (button.hasAttribute('data-gb-filter')) {
      this.filter = button.dataset.gbFilter;
      this.renderGrid(true);
    }
    if (button.hasAttribute('data-gb-even')) { this.even(); this.render(); }
    if (button.hasAttribute('data-gb-step')) this.showPanel(Number(button.dataset.gbStep));
    if (button.hasAttribute('data-gb-edit')) this.showPanel(Number(button.dataset.gbEdit));
    if (button.hasAttribute('data-gb-primary')) {
      if (this.state.panel < 2) this.showPanel(this.state.panel + 1);
      else this.submit('cart');
    }
    if (button.dataset.gbAction === 'another') this.submit('another');
    if (button.dataset.gbAction === 'double') {
      this.state.quantity = this.state.quantity === 2 ? 1 : 2;
      this.renderSummary();
      this.save();
    }
    if (button.hasAttribute('data-gb-single')) { this.state.quantity = 1; this.renderSummary(); this.save(); }
  }

  availableReports(handle) {
    const report = this.documents.reports.find((item) => item.handle === handle);
    if (!report) return [];
    const sources = new Set([...this.el('coa-source').content.querySelectorAll('a[href]')].map((link) => link.href));
    return [report, ...(report.supplements || [])].filter((item) => sources.has(item.original));
  }

  availableReport(handle) {
    return this.availableReports(handle)[0] || null;
  }

  openReader(tab, trigger) {
    this.readerTrigger = trigger;
    this.showDocumentTab(tab);
    this.readerOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.el('reader').showModal();
    this.el('reader-title').focus({ preventScroll: true });
  }

  onReaderClose() {
    document.documentElement.style.overflow = this.readerOverflow || '';
    this.readerTrigger?.focus({ preventScroll: true });
  }

  onReaderKeydown(event) {
    if (event.key !== 'Tab') return;
    const controls = [...this.el('reader').querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]')].filter((control) => control.getClientRects().length);
    if (!controls.length) return;
    const index = controls.indexOf(document.activeElement);
    if (index < 0 || (event.shiftKey && index === 0) || (!event.shiftKey && index === controls.length - 1)) {
      event.preventDefault();
      controls[event.shiftKey ? controls.length - 1 : 0].focus();
    }
  }

  documentPages(pages) {
    return `<div class="gb__document-pages" data-gb-document-pages>${pages.map((page, index) => `<figure class="gb__document-page" style="--gb-document-width:${Number(page.width)}px"><figcaption>${page.numbered === false ? '' : `<span>${String(index + 1).padStart(2, '0')}</span>`}${this.escape(page.caption || page.title)}</figcaption><div class="gb__document-image"><img src="${this.escape(page.url)}" alt="${this.escape(page.title)}" ${page.height ? `height="${Number(page.height)}"` : ''} width="${Number(page.width)}" loading="lazy" decoding="async"></div></figure>`).join('')}</div>`;
  }

  documentTools(count, report = false) {
    const tools = this.el('reader-tools');
    tools.hidden = false;
    tools.innerHTML = `<div class="gb__document-tools">${report ? `<button type="button" class="gb__text-button gb__document-back" data-gb-reports-back>${this.escape(this.text('document_back'))}</button>` : ''}<span class="gb__document-count">${this.escape(this.text(report ? 'document_page_count' : 'document_guide_count', {count}))}</span><button type="button" class="gb__document-zoom" data-gb-document-zoom aria-pressed="false">${this.escape(this.text('document_zoom'))}</button></div><p class="gb__document-zoom-hint" data-gb-document-zoom-hint hidden>${this.escape(this.text('document_zoom_hint'))}</p>`;
  }

  showDocumentTab(tab) {
    this.documentTab = tab === 'coa' ? 'coa' : 'guide';
    this.documentReport = null;
    this.el('reader-tools').hidden = true;
    this.el('reader-tools').innerHTML = '';
    this.all('document-tab').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.gbDocumentTab === this.documentTab)));
    this.el('reader-title').textContent = this.text('document_reader_title');
    const body = this.el('reader-body');
    if (this.documentTab === 'guide') {
      const pages = [...this.el('guide-source').content.querySelectorAll('img[src]')].filter((image) => {
        try { return new URL(image.src).hostname === 'cdn.shopify.com'; } catch { return false; }
      }).map((image) => {
        const labels = [['01_aromatherapy', 'guide_aromatherapy'], ['02_concentrates', 'guide_concentrates'], ['03_culinary', 'guide_culinary'], ['04a_flowers', 'guide_flower_herbs'], ['04b_flowers', 'guide_flower'], ['05_lotions', 'guide_skin_care']];
        const label = labels.find(([filename]) => image.src.includes(filename));
        return {url:image.src,title:image.alt || this.text('formulation_guide'),caption:label ? this.text(label[1]) : image.alt || this.text('formulation_guide'),width:Number(image.getAttribute('width')) || 600};
      });
      if (pages.length) this.documentTools(pages.length);
      body.innerHTML = `<h3>${this.escape(this.text('document_guide_heading'))}</h3><p>${this.escape(this.text('document_guide_intro'))}</p><p class="gb__document-note">${this.escape(this.text('document_guide_note'))}</p>${pages.length ? this.documentPages(pages) : `<p role="status">${this.escape(this.text('document_guide_unavailable'))}</p>`}`;
    } else {
      const selected = this.state.components.map((component) => this.byHandle.get(component.handle)).filter(Boolean);
      const published = this.documents.reports.filter((report) => this.availableReport(report.handle));
      const selectedHandles = new Set(selected.map((flavor) => flavor.handle));
      const library = published.filter((report) => !selectedHandles.has(report.handle)).map((report) => this.byHandle.get(report.handle) || report);
      const reportList = (items) => `<ul class="gb__document-list">${items.map((flavor) => {
        const report = this.availableReport(flavor.handle);
        const request = `mailto:TSLabs323@gmail.com?subject=${encodeURIComponent(this.text('document_request_subject', {name:flavor.title}))}&body=${encodeURIComponent(this.text('document_request_body', {name:flavor.title}))}`;
        return `<li>${flavor.img ? `<img src="${this.escape(this.image(flavor))}" alt="" width="30" height="52" loading="lazy">` : ''}<div><strong>${this.escape(flavor.title)}</strong><span>${this.escape(this.text(report ? 'document_report_available' : 'document_report_missing'))}</span></div>${report ? `<button type="button" class="gb__document-view" data-gb-report="${this.escape(report.handle)}" aria-label="${this.escape(this.text('document_view_named', {name:flavor.title}))}">${this.escape(this.text('document_view'))}</button>` : `<a class="gb__document-view" href="${this.escape(request)}" aria-label="${this.escape(this.text('document_request_named', {name:flavor.title}))}">${this.escape(this.text('document_request_profile'))}</a>`}</li>`;
      }).join('')}</ul>`;
      body.innerHTML = `<p>${this.escape(this.text('document_coa_intro'))}</p>${selected.length ? `<h3>${this.escape(this.text('document_coa_heading'))}</h3>${reportList(selected)}` : ''}${library.length ? `<h3 class="gb__document-library">${this.escape(this.text('document_coa_library'))}</h3>${reportList(library)}` : ''}<p class="gb__document-note">${this.escape(this.text('document_coa_note'))}</p><a class="gb__document-contact" href="mailto:TSLabs323@gmail.com">${this.escape(this.text('document_request'))}</a>`;
    }
    body.scrollTop = 0;
    if (this.el('reader').open) this.el('reader-title').focus({ preventScroll: true });
  }

  showReport(handle) {
    const reports = this.availableReports(handle);
    if (!reports.length) return;
    const name = this.byHandle.get(handle)?.title || reports[0].title;
    this.documentReport = handle;
    this.el('reader-title').textContent = this.text('document_report_title', {name});
    this.documentTools(reports.reduce((count, report) => count + report.pages.length, 0), true);
    this.el('reader-body').innerHTML = `<p class="gb__document-note">${this.escape(this.text('document_coa_note'))}</p>${reports.map((report, reportIndex) => {
      const pages = report.pages.map((page, index) => ({...page,numbered:false,title:this.text('document_page', {number:index+1,total:report.pages.length,name:report.title}),caption:this.text('document_page_caption', {number:index+1,total:report.pages.length})}));
      const label = `${report.title} · ${this.text('document_page_count', {count:pages.length})}`;
      return `<section class="gb__document-report"><h3>${this.escape(label)}</h3><button type="button" class="gb__text-button gb__document-download" data-gb-download-report="${this.escape(handle)}" data-gb-report-index="${reportIndex}" aria-label="${this.escape(this.text('document_download'))}: ${this.escape(label)}">${this.escape(this.text('document_download'))}</button><p data-gb-download-error role="alert" hidden></p>${this.documentPages(pages)}</section>`;
    }).join('')}`;
    this.el('reader-body').scrollTop = 0;
    this.el('reader-title').focus({ preventScroll: true });
  }

  async downloadReport(handle, button) {
    const report = this.availableReports(handle)[Number(button.dataset.gbReportIndex || 0)];
    if (!report) return;
    const error = button.parentElement.querySelector('[data-gb-download-error]');
    button.disabled = true;
    button.textContent = this.text('document_downloading');
    error.hidden = true;
    try {
      const response = await fetch(report.original);
      if (!response.ok) throw new Error('Document download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = new URL(report.original).pathname.split('/').pop();
      this.el('reader').appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (button.isConnected) {
        error.textContent = this.text('document_download_error');
        error.hidden = false;
      }
    } finally {
      button.disabled = false;
      button.textContent = this.text('document_download');
    }
  }

  onInput(event) {
    if (this.busy) return;
    const input = event.target;
    if (input.hasAttribute('data-gb-search')) {
      this.query = input.value.trim().toLowerCase();
      this.renderGrid(true);
    }
    if (input.hasAttribute('data-gb-name')) { this.state.name = input.value.slice(0, 60); this.renderSummary(); this.save(); }
    if (input.hasAttribute('data-gb-size')) { this.state.size = input.value; this.renderSummary(); this.save(); }
    if (input.hasAttribute('data-gb-profile')) { this.state.dank = input.value; this.renderSummary(); this.save(); }
    if (input.hasAttribute('data-gb-ratio')) {
      this.adjust(input.dataset.gbRatio, Number(input.value));
      this.syncRatios();
      this.renderSummary();
      this.save();
    }
  }

  showPanel(panel, focus = true) {
    if (![0, 1, 2].includes(panel) || (panel > 0 && !this.ready)) return;
    this.state.panel = panel;
    this.all('panel').forEach((element) => { element.hidden = Number(element.dataset.gbPanel) !== panel; });
    this.all('step').forEach((button) => {
      const step = Number(button.dataset.gbStep);
      button.disabled = step > 0 && !this.ready;
      button.classList.toggle('is-complete', step < panel);
      if (step === panel) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');
      button.querySelector('[data-gb-step-number]').innerHTML = step < panel ? this.icon('check') : String(step + 1);
    });
    this.renderSummary();
    this.save();
    if (focus) {
      const title = this.el('panel').parentElement.querySelector(`[data-gb-panel="${panel}"] h1`);
      title?.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }

  even() {
    const n = this.state.components.length;
    this.state.components.forEach((c, i) => { c.pct = Math.floor(100 / n) + (i < 100 % n ? 1 : 0); });
  }

  toggle(handle) {
    if (!this.byHandle.has(handle)) return;
    const index = this.state.components.findIndex((c) => c.handle === handle);
    if (index >= 0) this.state.components.splice(index, 1);
    else if (this.state.components.length < 6) this.state.components.push({ handle, pct: 0 });
    else return;
    this.even();
    this.state.quantity = 1;
    this.el('error').hidden = true;
    this.render();
    this.save();
  }

  adjust(handle, requested) {
    const comps = this.state.components;
    if (comps.length < 2) return;
    const target = comps.find((c) => c.handle === handle);
    if (!target || !Number.isFinite(requested)) return;
    target.pct = Math.min(100 - (comps.length - 1) * 5, Math.max(5, Math.round(requested)));
    const other = comps.filter((c) => c !== target);
    let remaining = 100 - target.pct - other.length * 5;
    const weight = other.reduce((sum, c) => sum + Math.max(0, c.pct - 5), 0);
    const shares = other.map((c) => remaining * (weight ? Math.max(0, c.pct - 5) / weight : 1 / other.length));
    const rounded = shares.map(Math.floor);
    let remainder = remaining - rounded.reduce((sum, value) => sum + value, 0);
    const order = shares.map((value, i) => ({ i, remainder: value - rounded[i] })).sort((a, b) => b.remainder - a.remainder);
    order.forEach(({ i }) => { if (remainder > 0) { rounded[i]++; remainder--; } });
    other.forEach((c, i) => { c.pct = 5 + rounded[i]; });
  }

  allocation() {
    const units = Math.round(this.ml * (100 - this.dankPct));
    const exact = this.state.components.map((c) => units * c.pct / 100);
    const rounded = exact.map(Math.floor);
    let left = units - rounded.reduce((sum, value) => sum + value, 0);
    exact.map((value, i) => ({ i, remainder: value - rounded[i] })).sort((a, b) => b.remainder - a.remainder).forEach(({ i }) => { if (left > 0) { rounded[i]++; left--; } });
    return rounded.map((value) => value / 100);
  }

  properties() {
    const ml = this.allocation();
    const props = {};
    if (this.state.name.trim()) props['Blend name'] = this.state.name.trim().slice(0, 60);
    props.Volume = this.state.size;
    props.Blend = this.state.components.map((c, i) => `${this.byHandle.get(c.handle).title} ${this.fmtMl(ml[i])}`).join(' · ');
    props.Dankness = this.dankPct ? `${this.state.dank} — ${this.dankPct}% (${this.fmtMl(this.ml * this.dankPct / 100)})` : this.state.dank;
    props._recipe = this.state.components.map((c, i) => `${c.handle}:${ml[i]}`).join('|');
    props._dank_pct = String(this.dankPct);
    props._size_ml = String(this.ml);
    props._v = '1';
    return props;
  }

  category(flavor) { return `<span class="gb__category" data-kind="${this.escape(flavor.category)}">${this.escape(this.text(flavor.category))}</span>`; }
  image(flavor) { return `${this.imageBase}${flavor.img}?width=180`; }

  ingredientLabel(flavor) {
    return this.flavors.some((other) => other.handle !== flavor.handle && other.title === flavor.title)
      ? `${flavor.title}, ${this.text(flavor.category)}` : flavor.title;
  }

  filteredFlavors() {
    return this.flavors.filter((f) => (this.filter === 'all' || (this.filter === 'strain' ? f.category !== 'flavor' : f.category === this.filter)) && `${f.title} ${this.text(f.category)} ${f.aroma || f.note || ''}`.toLowerCase().includes(this.query));
  }

  renderGrid(reset = false) {
    const filtered = this.filteredFlavors();
    this.all('filter').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.gbFilter === this.filter)));
    this.el('empty').hidden = filtered.length > 0;
    this.el('results').textContent = this.text(filtered.length === 1 ? 'ingredient_count_one' : 'ingredient_count', { count: filtered.length });
    const signature = `${this.filter}|${this.query}`;
    if (signature === this.gridSignature) {
      this.all('pick').forEach((button) => {
        const selected = this.state.components.some((c) => c.handle === button.dataset.gbPick);
        const flavor = this.byHandle.get(button.dataset.gbPick);
        button.setAttribute('aria-pressed', String(selected));
        button.setAttribute('aria-label', this.text(selected ? 'remove_profile' : 'add_profile', { title: this.ingredientLabel(flavor) }));
        button.disabled = !selected && this.state.components.length === 6;
        button.querySelector('.gb__pick').innerHTML = this.icon(selected ? 'check' : 'plus');
      });
      if (reset) this.el('rail').scrollLeft = 0;
      return;
    }
    this.gridSignature = signature;
    const cards = filtered.map((flavor) => {
      const selected = this.state.components.some((c) => c.handle === flavor.handle);
      return `<button type="button" class="gb__card" data-gb-pick="${this.escape(flavor.handle)}" aria-label="${this.escape(this.text(selected ? 'remove_profile' : 'add_profile', { title: this.ingredientLabel(flavor) }))}" aria-describedby="${this.id}-aroma-${this.escape(flavor.handle)}" aria-pressed="${selected}" ${!selected && this.state.components.length === 6 ? 'disabled' : ''}><span class="gb__pick">${this.icon(selected ? 'check' : 'plus')}</span><img src="${this.escape(this.image(flavor))}" alt="" width="63" height="108" loading="lazy"><strong>${this.escape(flavor.title)}</strong>${this.category(flavor)}<span class="gb__aroma" id="${this.id}-aroma-${this.escape(flavor.handle)}">${this.escape(flavor.aroma_short || '')}</span></button>`;
    });
    this.el('grid').innerHTML = Array.from({ length: Math.ceil(cards.length / 9) }, (_, index) => `<div class="gb__slide" data-gb-slide>${cards.slice(index * 9, index * 9 + 9).join('')}</div>`).join('');
    this.el('rail').scrollLeft = 0;
  }

  syncRatios() {
    this.all('ratio').forEach((input) => {
      const component = this.state.components.find((c) => c.handle === input.dataset.gbRatio);
      input.value = component.pct;
      input.previousElementSibling.querySelector('output').textContent = `${component.pct}%`;
    });
  }

  render() {
    this.el('selection-title').textContent = this.text(this.ready ? 'blend_ready' : this.state.components.length === 1 ? 'pick_one_more' : 'pick_two');
    this.el('selection').classList.toggle('is-empty', this.state.components.length === 0);
    this.el('selection-hint').hidden = this.state.components.length === 0;
    this.el('selection-hint').textContent = this.text(this.state.components.length === 6 ? 'continue_bottle' : this.ready ? 'ready_description' : 'optional_more');
    this.el('count').textContent = this.text('selected', { count: this.state.components.length });
    this.el('chips').innerHTML = this.state.components.map((c) => `<button type="button" class="gb__chip" data-gb-remove="${this.escape(c.handle)}" aria-label="${this.escape(this.text('remove_profile', { title: this.ingredientLabel(this.byHandle.get(c.handle)) }))}">${this.escape(this.byHandle.get(c.handle).title)}${this.icon('close')}</button>`).join('');
    this.el('balance').hidden = !this.ready;
    this.el('ratios').innerHTML = this.state.components.map((c) => `<div class="gb__ratio"><label for="${this.id}-${this.escape(c.handle)}">${this.escape(this.byHandle.get(c.handle).title)}<output>${c.pct}%</output></label><input type="range" id="${this.id}-${this.escape(c.handle)}" data-gb-ratio="${this.escape(c.handle)}" min="5" max="${100 - (this.state.components.length - 1) * 5}" step="1" value="${c.pct}"></div>`).join('');
    this.renderGrid();
    this.renderSummary();
    this.all('step').forEach((button) => { button.disabled = this.busy || (Number(button.dataset.gbStep) > 0 && !this.ready); });
  }

  renderSummary() {
    const variant = this.variant;
    const quantity = this.state.quantity;
    const totalQuantity = (this.customCartQuantity || 0) + quantity;
    const percentage = totalQuantity >= 4 ? 20 : totalQuantity >= 3 ? 15 : 10;
    const total = variant ? this.money(Math.round(variant.price * (100 - percentage) / 100) * quantity) : this.text('unavailable');
    this.el('bottle-price').textContent = variant ? this.money(Math.round(variant.price * 0.9)) : this.text('unavailable');
    this.el('profile-hint').textContent = this.text(['clean_description', 'dank_description', 'super_description'][[0, 3, 8].indexOf(this.dankPct)]);
    this.el('recipe').innerHTML = this.state.components.map((c) => {
      const flavor = this.byHandle.get(c.handle);
      return `<div class="gb__recipe-row"><img src="${this.escape(this.image(flavor))}" alt="" width="59" height="76"><div><strong>${this.escape(flavor.title)}</strong>${this.category(flavor)}<span class="gb__recipe-aroma">${this.escape(flavor.aroma_short || '')}</span></div><span>${c.pct}%</span></div>`;
    }).join('');
    const entries = [['size', this.state.size], ['quantity', quantity], ['price', total]];
    if (this.state.dank !== 'Clean') entries.splice(2, 0, ['profile', this.text(this.state.dank === 'Dank' ? 'dank' : 'super_dank')]);
    if (this.state.name.trim()) entries.unshift(['name_short', this.state.name.trim()]);
    this.el('summary').innerHTML = entries.map(([key, value]) => `<div><dt>${this.escape(this.text(key))}</dt><dd>${this.escape(value)}</dd></div>`).join('');
    this.el('receive').textContent = this.text(quantity === 2 ? 'receive_two' : 'receive_one', { size: this.state.size });
    this.querySelector('[data-gb-action="double"]').setAttribute('aria-pressed', String(quantity === 2));
    this.el('single').hidden = quantity !== 2;
    this.el('offer-note').hidden = quantity !== 2;
    this.el('offer-note').textContent = this.text('double_description');
    this.el('dock-price').textContent = variant && this.ready ? total : '';
    this.el('dock-description').textContent = this.ready ? this.text(quantity === 2 ? 'two_bottles_size' : 'one_bottle_size', { size: this.state.size }) : this.text(this.state.components.length === 1 ? 'pick_one_more' : 'pick_two');
    this.el('primary-text').textContent = this.busy ? this.text('adding') : this.text(this.state.panel === 0 ? 'continue_bottle' : this.state.panel === 1 ? 'continue_review' : quantity === 2 ? 'add_both' : 'add_cart');
    this.el('primary').disabled = this.busy || !this.ready || !variant;
    this.all('action').forEach((button) => { button.disabled = this.busy || !this.ready || !variant; });
    this.el('single').disabled = this.busy;
    this.querySelectorAll('input, select').forEach((input) => { input.disabled = this.busy; });
  }

  showError(message) {
    this.el('error').textContent = message;
    this.el('error').hidden = false;
    this.el('error').scrollIntoView({ block: 'nearest' });
  }

  async submit(action) {
    if (this.busy || !this.ready || !this.variant || this.state.components.reduce((sum, c) => sum + c.pct, 0) !== 100) return;
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    this.el('error').hidden = true;
    this.renderSummary();
    let added = false;
    try {
      const quantity = action === 'another' ? 1 : this.state.quantity;
      const selected = await Promise.all(this.state.components.map((component) => this.json(`${this.root}products/${encodeURIComponent(component.handle)}.js`)));
      const unavailable = selected.filter((product) => {
        const source = product.variants.find((v) => v.option1 === '120ml' || v.title === '120ml');
        return source ? !source.available : !product.available;
      });
      if (unavailable.length) throw new Error(this.text('sold_out', { titles: unavailable.map((p) => p.title).join(', ') }));
      const product = await this.json(`${this.dataset.productUrl}.js`);
      const variant = product.variants.find((v) => v.option1 === this.state.size && v.option2 === this.state.dank && v.available);
      if (!variant) throw new Error(this.text('unavailable'));
      if (variant.price !== this.variant.price) {
        this.variants = product.variants;
        throw new Error(this.text('price_changed'));
      }
      const properties = this.properties();
      const recipeTitle = this.state.name.trim() || this.state.components.map((c) => this.byHandle.get(c.handle).title).join(' + ');
      await this.json(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variant.id, quantity, properties }] }) });
      added = true;
      this.customCartQuantity = (this.customCartQuantity || 0) + quantity;
      try { sessionStorage.removeItem('tsl-guided-draft-v1'); } catch {}
      if (action === 'another') {
        this.state.components = [];
        this.state.name = '';
        this.state.quantity = 1;
        this.el('name').value = '';
        this.el('balance').open = false;
        this.filter = 'all';
        this.query = '';
        this.el('search').value = '';
        this.el('saved-title').textContent = this.text('blend_added');
        this.el('saved-recipe').textContent = recipeTitle;
        this.el('saved-detail').textContent = this.text('saved_bottle', { size: this.state.size });
        this.el('saved-next').textContent = this.text('saved_next');
        this.el('notice').hidden = false;
        this.el('view-cart').hidden = false;
        this.render();
        this.showPanel(0);
        this.el('saved-title').focus({ preventScroll: true });
      } else {
        window.location.assign(this.dataset.cartUrl || `${this.root}cart`);
      }
    } catch (error) {
      this.showError(added ? this.text('already_added') : this.text('add_error', { error: error.message }));
      console.error('[guided-blender] add failed', error.message);
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.renderSummary();
    }
  }
}
if (!customElements.get('tsl-guided-blender')) customElements.define('tsl-guided-blender', GuidedBlender);
